import React, { useState, useEffect, useCallback } from 'react';
import { X, Copy, ExternalLink, RefreshCw, CheckCircle, AlertCircle, Globe, Link2, BarChart3 } from 'lucide-react';
import { Button } from '../ui/Button';
import { apiService } from '../../services/api';

interface Client {
  id: string;
  username: string;
  password: string;
  member_id: number;
  package_id?: number;
  exp_date?: number;
  max_connections: number;
  is_trial: number;
  system: number;
  enabled: number;
  p2p_id?: number;
  p2p_status?: string;
  created_at: string;
  updated_at: string;
  member?: {
    id: number;
    username: string;
    email: string;
    status: number;
  };
  package?: {
    id: number;
    package_name: string;
    official_credits: number;
    official_duration: number;
  };
}

interface ClientUrls {
  domain: string;
  urls: {
    mpegts: { label: string; description: string; url: string };
    m3u8: { label: string; description: string; url: string };
    hls: { label: string; description: string; url: string };
    ssiptv: { label: string; description: string; url: string };
  };
  generatedAt: string;
}

interface ShortUrl {
  shortCode: string;
  originalUrl: string;
  urlType: string;
  clientId: string;
  clientUsername: string;
  createdBy: string;
  mysqlUserId: number;
  domain: string;
  isActive: boolean;
  accessCount: number;
  lastAccessed: string | null;
  expiresAt: string | null;
  createdAt: string;
  updatedAt: string;
  fullShortUrl?: string;
}

interface ClientDetailsModalProps {
  client: Client;
  isOpen: boolean;
  onClose: () => void;
}

const ClientDetailsModal: React.FC<ClientDetailsModalProps> = ({
  client,
  isOpen,
  onClose
}) => {
  const [clientUrls, setClientUrls] = useState<ClientUrls | null>(null);
  const [shortUrls, setShortUrls] = useState<ShortUrl[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isGeneratingShortUrls, setIsGeneratingShortUrls] = useState(false);
  const [copiedUrls, setCopiedUrls] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'original' | 'short'>('original');

  const fetchClientUrls = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await apiService.getClientUrls(client.id);
      
      if (response.success) {
        setClientUrls(response.data);
      } else {
        setError(response.error || 'Erro ao carregar URLs do cliente');
      }
    } catch (error) {
      console.error('Erro ao buscar URLs do cliente:', error);
      setError('Erro ao carregar URLs do cliente');
    } finally {
      setIsLoading(false);
    }
  }, [client.id]);

  const fetchShortUrls = useCallback(async () => {
    try {
      const response = await apiService.getClientShortUrls(client.id);
      
      if (response.success) {
        setShortUrls(response.data);
      }
    } catch (error) {
      console.error('Erro ao buscar URLs encurtadas:', error);
    }
  }, [client.id]);

  useEffect(() => {
    if (isOpen && client) {
      fetchClientUrls();
      fetchShortUrls();
    }
  }, [isOpen, client, fetchClientUrls, fetchShortUrls]);


  const copyToClipboard = async (url: string, type: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedUrls(prev => new Set(prev).add(type));
      
      // Remover o indicador após 2 segundos
      setTimeout(() => {
        setCopiedUrls(prev => {
          const newSet = new Set(prev);
          newSet.delete(type);
          return newSet;
        });
      }, 2000);
    } catch (error) {
      console.error('Erro ao copiar para clipboard:', error);
      alert('Erro ao copiar URL');
    }
  };

  const openUrl = (url: string) => {
    window.open(url, '_blank');
  };

  const generateShortUrls = async () => {
    if (!clientUrls) return;

    try {
      setIsGeneratingShortUrls(true);
      setError(null);

      const response = await apiService.generateClientShortUrls(
        client.id,
        clientUrls.domain
      );

      if (response.success) {
        // Recarregar URLs encurtadas
        await fetchShortUrls();
        setActiveTab('short');
        alert('URLs encurtadas geradas com sucesso!');
      } else {
        setError(response.error || 'Erro ao gerar URLs encurtadas');
      }
    } catch (error) {
      console.error('Erro ao gerar URLs encurtadas:', error);
      setError('Erro ao gerar URLs encurtadas');
    } finally {
      setIsGeneratingShortUrls(false);
    }
  };


  const getShortUrlDisplayUrl = (shortCode: string, fullShortUrl?: string) => {
    // Se o backend forneceu a URL completa, usar ela
    if (fullShortUrl) {
      return fullShortUrl;
    }
    
    // Fallback para desenvolvimento
    const baseUrl = window.location.origin;
    return `${baseUrl}/s/${shortCode}`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-100';
      case 'inactive': return 'text-red-600 bg-red-100';
      case 'suspended': return 'text-orange-600 bg-orange-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active': return 'Ativo';
      case 'inactive': return 'Inativo';
      case 'suspended': return 'Suspenso';
      default: return status;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 sm:p-6 border-b border-gray-200 gap-4">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="p-2 bg-blue-100 rounded-full flex-shrink-0">
              <Globe className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">
                URLs de Acesso do Cliente
              </h2>
              <p className="text-xs sm:text-sm text-gray-600 truncate">
                {client.username} • {client.member?.email || 'Email não disponível'}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                fetchClientUrls();
                fetchShortUrls();
              }}
              disabled={isLoading}
              className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm"
            >
              <RefreshCw className={`h-3 w-3 sm:h-4 sm:w-4 ${isLoading ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Atualizar</span>
            </Button>
            {clientUrls && (
              <Button
                variant="outline"
                size="sm"
                onClick={generateShortUrls}
                disabled={isGeneratingShortUrls}
                className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm"
              >
                <Link2 className={`h-3 w-3 sm:h-4 sm:w-4 ${isGeneratingShortUrls ? 'animate-pulse' : ''}`} />
                <span className="hidden sm:inline">{isGeneratingShortUrls ? 'Gerando...' : 'Encurtar URLs'}</span>
                <span className="sm:hidden">{isGeneratingShortUrls ? '...' : 'Encurtar'}</span>
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={onClose}
              className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm"
            >
              <X className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Fechar</span>
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(95vh-140px)] sm:max-h-[calc(90vh-120px)]">
          {/* Client Info */}
          <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-gray-50 rounded-lg">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-gray-900 text-sm sm:text-base">Informações do Cliente</h3>
                <div className="mt-2 space-y-1">
                  <p className="text-xs sm:text-sm text-gray-600 truncate">
                    <span className="font-medium">Username:</span> {client.username}
                  </p>
                  <p className="text-xs sm:text-sm text-gray-600 truncate">
                    <span className="font-medium">Email:</span> {client.member?.email || 'Email não disponível'}
                  </p>
                </div>
              </div>
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs sm:text-sm font-medium ${getStatusColor(client.enabled === 1 ? 'active' : 'inactive')} flex-shrink-0`}>
                {getStatusLabel(client.enabled === 1 ? 'active' : 'inactive')}
              </span>
            </div>
          </div>

          {/* Domain Info */}
          {clientUrls && (
            <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-blue-50 rounded-lg">
              <div className="flex items-center gap-2">
                <Globe className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 flex-shrink-0" />
                <h3 className="font-medium text-blue-900 text-sm sm:text-base">Domínio Utilizado</h3>
              </div>
              <p className="text-xs sm:text-sm text-blue-700 mt-1 break-all">{clientUrls.domain}</p>
              <p className="text-xs text-blue-600 mt-1">
                URLs geradas em: {new Date(clientUrls.generatedAt).toLocaleString()}
              </p>
            </div>
          )}

          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="flex items-center gap-3">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <span className="text-gray-600">Carregando URLs...</span>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-3" />
                <p className="text-red-600 font-medium">Erro ao carregar URLs</p>
                <p className="text-gray-600 text-sm mt-1">{error}</p>
                <Button
                  onClick={fetchClientUrls}
                  className="mt-3"
                  size="sm"
                >
                  Tentar Novamente
                </Button>
              </div>
            </div>
          )}

          {/* Tabs */}
          {clientUrls && !isLoading && (
            <div className="mb-4 sm:mb-6">
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-2 sm:space-x-8 overflow-x-auto">
                  <button
                    onClick={() => setActiveTab('original')}
                    className={`py-2 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap ${
                      activeTab === 'original'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Globe className="h-3 w-3 sm:h-4 sm:w-4 inline mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">URLs Originais</span>
                    <span className="sm:hidden">Originais</span>
                  </button>
                  <button
                    onClick={() => setActiveTab('short')}
                    className={`py-2 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap ${
                      activeTab === 'short'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Link2 className="h-3 w-3 sm:h-4 sm:w-4 inline mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">URLs Encurtadas</span>
                    <span className="sm:hidden">Encurtadas</span>
                    {shortUrls.length > 0 && (
                      <span className="ml-1 bg-blue-100 text-blue-600 text-xs px-1 sm:px-2 py-0.5 sm:py-1 rounded-full">
                        {shortUrls.length}
                      </span>
                    )}
                  </button>
                </nav>
              </div>
            </div>
          )}

          {/* URLs List */}
          {clientUrls && !isLoading && activeTab === 'original' && (
            <div className="space-y-3 sm:space-y-4">
              {Object.entries(clientUrls.urls).map(([type, urlData]) => (
                <div key={type} className="border border-gray-200 rounded-lg p-3 sm:p-4">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-3 gap-3">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-gray-900 text-sm sm:text-base">{urlData.label}</h4>
                      <p className="text-xs sm:text-sm text-gray-600">{urlData.description}</p>
                    </div>
                    <div className="flex items-center gap-2 w-full sm:w-auto">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openUrl(urlData.url)}
                        className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm flex-1 sm:flex-none"
                      >
                        <ExternalLink className="h-3 w-3 sm:h-4 sm:w-4" />
                        <span className="hidden sm:inline">Abrir</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyToClipboard(urlData.url, type)}
                        className={`flex items-center gap-1 sm:gap-2 text-xs sm:text-sm flex-1 sm:flex-none ${
                          copiedUrls.has(type) ? 'bg-green-50 border-green-200 text-green-700' : ''
                        }`}
                      >
                        {copiedUrls.has(type) ? (
                          <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4" />
                        ) : (
                          <Copy className="h-3 w-3 sm:h-4 sm:w-4" />
                        )}
                        <span className="hidden sm:inline">{copiedUrls.has(type) ? 'Copiado!' : 'Copiar'}</span>
                        <span className="sm:hidden">{copiedUrls.has(type) ? 'Copiado' : 'Copiar'}</span>
                      </Button>
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-md p-2 sm:p-3">
                    <code className="text-xs sm:text-sm text-gray-800 break-all">{urlData.url}</code>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Short URLs List */}
          {clientUrls && !isLoading && activeTab === 'short' && (
            <div className="space-y-3 sm:space-y-4">
              {shortUrls.length === 0 ? (
                <div className="text-center py-6 sm:py-8">
                  <Link2 className="h-8 w-8 sm:h-12 sm:w-12 text-gray-400 mx-auto mb-3 sm:mb-4" />
                  <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">
                    Nenhuma URL encurtada
                  </h3>
                  <p className="text-sm sm:text-base text-gray-600 mb-4 px-4">
                    Gere URLs encurtadas para facilitar o compartilhamento e rastrear acessos.
                  </p>
                  <Button
                    onClick={generateShortUrls}
                    disabled={isGeneratingShortUrls}
                    className="flex items-center gap-2 mx-auto text-sm"
                  >
                    <Link2 className={`h-3 w-3 sm:h-4 sm:w-4 ${isGeneratingShortUrls ? 'animate-pulse' : ''}`} />
                    <span className="hidden sm:inline">{isGeneratingShortUrls ? 'Gerando...' : 'Gerar URLs Encurtadas'}</span>
                    <span className="sm:hidden">{isGeneratingShortUrls ? 'Gerando...' : 'Gerar URLs'}</span>
                  </Button>
                </div>
              ) : (
                shortUrls.map((shortUrl) => {
                  const urlData = clientUrls.urls[shortUrl.urlType as keyof typeof clientUrls.urls];
                  const displayUrl = getShortUrlDisplayUrl(shortUrl.shortCode, shortUrl.fullShortUrl);
                  
                  return (
                    <div key={shortUrl.shortCode} className="border border-gray-200 rounded-lg p-3 sm:p-4">
                      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-3 gap-3">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-gray-900 text-sm sm:text-base">{urlData?.label || shortUrl.urlType}</h4>
                          <p className="text-xs sm:text-sm text-gray-600">{urlData?.description}</p>
                        </div>
                        <div className="flex items-center gap-2 w-full sm:w-auto">
                          <div className="flex items-center gap-1 text-xs sm:text-sm text-gray-600">
                            <BarChart3 className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                            <span className="hidden sm:inline">{shortUrl.accessCount} acessos</span>
                            <span className="sm:hidden">{shortUrl.accessCount}</span>
                          </div>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium flex-shrink-0 ${
                            shortUrl.isActive 
                              ? 'text-green-600 bg-green-100' 
                              : 'text-red-600 bg-red-100'
                          }`}>
                            {shortUrl.isActive ? 'Ativo' : 'Inativo'}
                          </span>
                        </div>
                      </div>

                      {/* Short URL */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <label className="text-xs sm:text-sm font-medium text-gray-700">URL Encurtada:</label>
                        </div>
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                          <input
                            type="text"
                            value={displayUrl}
                            readOnly
                            className="flex-1 px-2 sm:px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-xs sm:text-sm font-mono"
                          />
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => copyToClipboard(displayUrl, `short-${shortUrl.shortCode}`)}
                              className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm flex-1 sm:flex-none"
                            >
                              {copiedUrls.has(`short-${shortUrl.shortCode}`) ? (
                                <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 text-green-600" />
                              ) : (
                                <Copy className="h-3 w-3 sm:h-4 sm:w-4" />
                              )}
                              <span className="hidden sm:inline">Copiar</span>
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openUrl(displayUrl)}
                              className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm flex-1 sm:flex-none"
                            >
                              <ExternalLink className="h-3 w-3 sm:h-4 sm:w-4" />
                              <span className="hidden sm:inline">Abrir</span>
                            </Button>
                          </div>
                        </div>
                      </div>

                      {/* Original URL */}
                      <div className="space-y-2 mt-3">
                        <div className="flex items-center gap-2">
                          <label className="text-xs sm:text-sm font-medium text-gray-700">URL Original:</label>
                        </div>
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                          <input
                            type="text"
                            value={shortUrl.originalUrl}
                            readOnly
                            className="flex-1 px-2 sm:px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-xs font-mono"
                          />
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => copyToClipboard(shortUrl.originalUrl, `original-${shortUrl.shortCode}`)}
                              className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm flex-1 sm:flex-none"
                            >
                              {copiedUrls.has(`original-${shortUrl.shortCode}`) ? (
                                <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 text-green-600" />
                              ) : (
                                <Copy className="h-3 w-3 sm:h-4 sm:w-4" />
                              )}
                              <span className="hidden sm:inline">Copiar</span>
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openUrl(shortUrl.originalUrl)}
                              className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm flex-1 sm:flex-none"
                            >
                              <ExternalLink className="h-3 w-3 sm:h-4 sm:w-4" />
                              <span className="hidden sm:inline">Abrir</span>
                            </Button>
                          </div>
                        </div>
                      </div>

                      {/* Stats */}
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between text-xs sm:text-sm text-gray-600 gap-1 sm:gap-0">
                          <div>
                            Criado em: {new Date(shortUrl.createdAt).toLocaleDateString('pt-BR')}
                          </div>
                          {shortUrl.lastAccessed && (
                            <div>
                              Último acesso: {new Date(shortUrl.lastAccessed).toLocaleDateString('pt-BR')}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* Instructions */}
          {clientUrls && (
            <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-yellow-50 rounded-lg">
              <h4 className="font-medium text-yellow-900 mb-2 text-sm sm:text-base">Como usar:</h4>
              <ul className="text-xs sm:text-sm text-yellow-800 space-y-1">
                <li>• <strong>MPEG-TS:</strong> Formato padrão para a maioria dos players IPTV</li>
                <li>• <strong>M3U8:</strong> Formato HLS para players compatíveis</li>
                <li>• <strong>HLS:</strong> Ideal para Hoku e outros players específicos</li>
                <li>• <strong>SS IPTV:</strong> Para usar no aplicativo SS IPTV</li>
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientDetailsModal;
